import React, { useCallback, useEffect, useState } from "react";

// ──────────────────────────────────────────────────────
// AdminApiUsage.jsx — 공개 API v1 키·사용량 (2026-10-06 개편)
//   LCS 사무소(그룹)마다 키를 따로 발급해 달별 사용량으로 사용료를 따로 낸다.
//   GET  /api/onto/admin/api-keys?month=YYYY-MM        키 목록 + 그 달·오늘 사용량
//   GET  /api/onto/admin/api-keys/{id}/daily?month=…   날짜별
//   POST /api/onto/admin/api-keys                      발급(키는 응답에 한 번만)
//   POST /api/onto/admin/api-keys/{id}                 이름·그룹·메모·한도
//   POST /api/onto/admin/api-keys/{id}/revoke          폐기
// ──────────────────────────────────────────────────────

function authHeaders(json = false) {
  const token = localStorage.getItem("admin_token");
  return { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(json ? { "Content-Type": "application/json" } : {}) };
}

const num = (v) => (v == null ? "—" : Number(v).toLocaleString("ko-KR"));
const fmtDateTime = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  if (isNaN(d.getTime())) return String(v).slice(0, 16);
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

export function monthOptions(now = new Date(), count = 12) {
  const out = [];
  for (let i = 0; i < count; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  }
  return out;
}

const EMPTY_FORM = { name: "", group_id: "", memo: "", rate_per_min: "120", daily_items: "50000" };

export default function AdminApiUsage({ API_BASE_URL = "", fetchImpl = fetch }) {
  const months = monthOptions();
  const [month, setMonth] = useState(months[0]);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [showRevoked, setShowRevoked] = useState(false);
  const [form, setForm] = useState(null);          // 발급 폼
  const [issued, setIssued] = useState(null);      // 방금 발급한 키(한 번만 보임)
  const [edit, setEdit] = useState(null);          // {id, name, group_id, memo, rate_per_min, daily_items}
  const [daily, setDaily] = useState(null);        // {id, name, days}
  const [message, setMessage] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetchImpl(`${API_BASE_URL}/api/onto/admin/api-keys?month=${month}`, { headers: authHeaders() });
      if (r.status === 401 || r.status === 403) { setError("관리자 로그인이 필요합니다(토큰 만료일 수 있습니다)"); setData(null); return; }
      const j = await r.json();
      if (!r.ok) { setError(j.error || `오류 ${r.status}`); return; }
      setData(j); setError(null);
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, fetchImpl, month]);

  useEffect(() => { load(); }, [load]);

  const post = async (path, body) => {
    const r = await fetchImpl(`${API_BASE_URL}/api/onto/admin/api-keys${path}`, { method: "POST", headers: authHeaders(true), body: JSON.stringify(body || {}) });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(j.error || `오류 ${r.status}`);
    return j;
  };

  const submitIssue = async () => {
    setMessage(null);
    try {
      const j = await post("", form);
      setIssued({ key: j.key, name: form.name });
      setForm(null);
      await load();
    } catch (e) { setMessage(e.message); }
  };

  const submitEdit = async () => {
    setMessage(null);
    try {
      const { id, ...body } = edit;
      await post(`/${id}`, body);
      setEdit(null);
      await load();
    } catch (e) { setMessage(e.message); }
  };

  const revoke = async (k) => {
    if (!window.confirm(`'${k.name}' 키를 폐기할까요? 이 키로 오는 요청은 바로 거절됩니다.`)) return;
    setMessage(null);
    try { await post(`/${k.id}/revoke`); await load(); } catch (e) { setMessage(e.message); }
  };

  const openDaily = async (k) => {
    if (daily && daily.id === k.id) { setDaily(null); return; }
    try {
      const r = await fetchImpl(`${API_BASE_URL}/api/onto/admin/api-keys/${k.id}/daily?month=${month}`, { headers: authHeaders() });
      const j = await r.json();
      setDaily({ id: k.id, name: k.name, days: Array.isArray(j.days) ? j.days : [] });
    } catch (e) { setMessage(String(e?.message || e)); }
  };

  const defRate = data?.default_rate_per_min;
  const defDaily = data?.default_daily_items;
  const f = filter.trim().toLowerCase();
  const keys = (data?.keys || [])
    .filter((k) => showRevoked || !k.revoked_at)
    .filter((k) => !f || [k.name, k.group_id, k.user_id, k.memo, k.key_prefix].some((v) => String(v || "").toLowerCase().includes(f)));
  const sum = keys.reduce((a, k) => ({ items: a.items + (k.month_items || 0), req: a.req + (k.month_requests || 0), rej: a.rej + (k.month_rejected || 0), today: a.today + (k.today_items || 0) }), { items: 0, req: 0, rej: 0, today: 0 });

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <h2 className="text-lg font-bold text-gray-800">
          API 키·사용량{" "}
          <span className="text-sm font-normal text-gray-500">공개 API v1 — 사무소(그룹)별 키, 달별 건수가 사용료 근거</span>
        </h2>
        <div className="flex items-center gap-2">
          <input className="border rounded px-2 py-1 text-sm w-44" placeholder="이름/그룹/메모 찾기" value={filter} onChange={(e) => setFilter(e.target.value)} />
          <select aria-label="달" className="border rounded px-2 py-1 text-sm" value={month} onChange={(e) => setMonth(e.target.value)}>
            {months.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <label className="text-sm text-gray-600 flex items-center gap-1">
            <input type="checkbox" checked={showRevoked} onChange={(e) => setShowRevoked(e.target.checked)} /> 폐기 키
          </label>
          <button onClick={load} className="border rounded px-3 py-1 text-sm hover:bg-gray-50">새로고침</button>
          <button onClick={() => { setForm({ ...EMPTY_FORM }); setIssued(null); }} className="rounded px-3 py-1 text-sm text-white" style={{ background: "#1a56db" }}>키 발급</button>
        </div>
      </div>
      <p className="text-xs text-gray-500 mb-3">
        한도가 비어 있으면 기본값(분당 {num(defRate)}회 · 하루 {num(defDaily)}건). 건수는 분류에 성공한 품목 수, 거절은 한도 초과로 막힌 요청 수(한국 시간 기준 달).
      </p>

      {error && <div className="text-sm border border-gray-300 bg-gray-50 rounded p-3 mb-3" role="alert">{error}</div>}
      {message && <div className="text-sm border border-gray-300 bg-gray-50 rounded p-3 mb-3" role="alert">{message}</div>}

      {issued && (
        <div data-testid="issued-key" className="rounded p-3 mb-3 border" style={{ borderColor: "#1a56db" }}>
          <div className="text-sm font-semibold mb-1">'{issued.name}' 키를 발급했습니다 — 이 키는 지금 한 번만 보입니다.</div>
          <div className="flex items-center gap-2">
            <code className="font-mono text-sm break-all" style={{ color: "#1a56db" }}>{issued.key}</code>
            <button className="border rounded px-2 py-0.5 text-xs" onClick={() => navigator.clipboard?.writeText(issued.key)}>복사</button>
            <button className="border rounded px-2 py-0.5 text-xs" onClick={() => setIssued(null)}>닫기</button>
          </div>
          <div className="text-xs text-gray-500 mt-1">메신저·메일·저장소에 붙이지 말고, LCS 서버 설정에 안전한 방법으로 넣으세요. 잃어버리면 폐기하고 다시 발급합니다.</div>
        </div>
      )}

      {form && (
        <div data-testid="issue-form" className="border rounded p-3 mb-3 bg-gray-50">
          <div className="text-sm font-semibold mb-2">새 키 발급</div>
          <div className="grid gap-2" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))" }}>
            <Field label="이름" value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="예: LCS 1005" />
            <Field label="LCS 그룹(사무소 코드)" value={form.group_id} onChange={(v) => setForm({ ...form, group_id: v })} placeholder="예: 1005" />
            <Field label="분당 요청 한도" value={form.rate_per_min} onChange={(v) => setForm({ ...form, rate_per_min: v })} placeholder={`비우면 ${defRate ?? ""}`} />
            <Field label="하루 건수 한도" value={form.daily_items} onChange={(v) => setForm({ ...form, daily_items: v })} placeholder={`비우면 ${defDaily ?? ""}`} />
            <Field label="메모(요금제·담당 등)" value={form.memo} onChange={(v) => setForm({ ...form, memo: v })} />
          </div>
          <div className="flex gap-2 mt-2">
            <button onClick={submitIssue} className="rounded px-3 py-1 text-sm text-white" style={{ background: "#1a56db" }}>발급</button>
            <button onClick={() => setForm(null)} className="border rounded px-3 py-1 text-sm">취소</button>
          </div>
        </div>
      )}

      {loading && !data ? (
        <div className="text-sm text-gray-500 p-4">불러오는 중…</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm border-t">
            <thead>
              <tr className="text-left text-gray-500 border-b bg-gray-50">
                <th className="px-3 py-2">이름</th>
                <th className="px-3 py-2">그룹</th>
                <th className="px-3 py-2">키</th>
                <th className="px-3 py-2 text-right">분당 한도</th>
                <th className="px-3 py-2 text-right">하루 한도</th>
                <th className="px-3 py-2 text-right">{month} 건수</th>
                <th className="px-3 py-2 text-right">요청</th>
                <th className="px-3 py-2 text-right">거절</th>
                <th className="px-3 py-2 text-right">오늘</th>
                <th className="px-3 py-2">마지막 사용</th>
                <th className="px-3 py-2">메모</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {keys.length === 0 && (
                <tr><td colSpan={12} className="px-3 py-6 text-center text-gray-500">키가 없습니다.</td></tr>
              )}
              {keys.map((k) => (
                <React.Fragment key={k.id}>
                  <tr data-testid={`key-row-${k.id}`} className={`border-b hover:bg-gray-50 ${k.revoked_at ? "opacity-50" : ""}`}>
                    <td className="px-3 py-2">{k.name}{k.user_id && k.user_id !== "admin" ? <span className="text-xs text-gray-500"> · {k.user_id}</span> : null}</td>
                    <td className="px-3 py-2">{k.group_id || "—"}</td>
                    <td className="px-3 py-2 font-mono text-xs">{k.key_prefix}…</td>
                    <td className="px-3 py-2 text-right">{k.rate_per_min ? num(k.rate_per_min) : <span className="text-gray-400">{num(defRate)}</span>}</td>
                    <td className="px-3 py-2 text-right">{k.daily_items ? num(k.daily_items) : <span className="text-gray-400">{num(defDaily)}</span>}</td>
                    <td className="px-3 py-2 text-right font-semibold">{num(k.month_items)}</td>
                    <td className="px-3 py-2 text-right">{num(k.month_requests)}</td>
                    <td className="px-3 py-2 text-right">{k.month_rejected ? <b>{num(k.month_rejected)}</b> : "0"}</td>
                    <td className="px-3 py-2 text-right">{num(k.today_items)}</td>
                    <td className="px-3 py-2 text-gray-500">{fmtDateTime(k.last_used_at)}</td>
                    <td className="px-3 py-2 text-gray-600 text-xs">{k.revoked_at ? `폐기 ${fmtDateTime(k.revoked_at).slice(0, 10)}` : k.memo || ""}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <button className="text-xs underline mr-2" onClick={() => openDaily(k)}>날짜별</button>
                      {!k.revoked_at && <button className="text-xs underline mr-2" onClick={() => setEdit({ id: k.id, name: k.name, group_id: k.group_id || "", memo: k.memo || "", rate_per_min: k.rate_per_min ?? "", daily_items: k.daily_items ?? "" })}>바꾸기</button>}
                      {!k.revoked_at && <button className="text-xs underline" onClick={() => revoke(k)}>폐기</button>}
                    </td>
                  </tr>
                  {edit && edit.id === k.id && (
                    <tr className="border-b bg-gray-50"><td colSpan={12} className="px-3 py-2">
                      <div className="grid gap-2" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))" }}>
                        <Field label="이름" value={edit.name} onChange={(v) => setEdit({ ...edit, name: v })} />
                        <Field label="그룹" value={edit.group_id} onChange={(v) => setEdit({ ...edit, group_id: v })} />
                        <Field label="분당 한도(비우면 기본)" value={String(edit.rate_per_min)} onChange={(v) => setEdit({ ...edit, rate_per_min: v })} />
                        <Field label="하루 한도(비우면 기본)" value={String(edit.daily_items)} onChange={(v) => setEdit({ ...edit, daily_items: v })} />
                        <Field label="메모" value={edit.memo} onChange={(v) => setEdit({ ...edit, memo: v })} />
                      </div>
                      <div className="flex gap-2 mt-2">
                        <button onClick={submitEdit} className="rounded px-3 py-1 text-sm text-white" style={{ background: "#1a56db" }}>저장</button>
                        <button onClick={() => setEdit(null)} className="border rounded px-3 py-1 text-sm">취소</button>
                      </div>
                    </td></tr>
                  )}
                  {daily && daily.id === k.id && (
                    <tr className="border-b"><td colSpan={12} className="px-3 py-2">
                      <div data-testid="daily-usage" className="text-xs">
                        <b>{daily.name}</b> · {month} 날짜별
                        {daily.days.length === 0 ? <span className="text-gray-500"> — 사용 없음</span> : (
                          <table className="mt-1 text-xs">
                            <thead><tr className="text-gray-500"><th className="pr-4 text-left">날짜</th><th className="pr-4 text-right">건수</th><th className="pr-4 text-right">요청</th><th className="text-right">거절</th></tr></thead>
                            <tbody>{daily.days.map((d) => (
                              <tr key={d.day}><td className="pr-4">{d.day}</td><td className="pr-4 text-right">{num(d.items)}</td><td className="pr-4 text-right">{num(d.requests)}</td><td className="text-right">{num(d.rejected)}</td></tr>
                            ))}</tbody>
                          </table>
                        )}
                      </div>
                    </td></tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
            {keys.length > 0 && (
              <tfoot>
                <tr className="border-t-2 font-semibold bg-gray-50">
                  <td className="px-3 py-2" colSpan={5}>합계 ({keys.length}개 키)</td>
                  <td className="px-3 py-2 text-right">{num(sum.items)}</td>
                  <td className="px-3 py-2 text-right">{num(sum.req)}</td>
                  <td className="px-3 py-2 text-right">{num(sum.rej)}</td>
                  <td className="px-3 py-2 text-right">{num(sum.today)}</td>
                  <td className="px-3 py-2" colSpan={3}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, placeholder = "" }) {
  return (
    <label className="text-xs text-gray-600 flex flex-col gap-1">
      {label}
      <input aria-label={label} className="border rounded px-2 py-1 text-sm text-gray-900" value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}
